import { syncRowPhoneButton } from "./candidate_row_sync";

let bound = false;
const PHONE_FETCH_POLL_INTERVAL_MS = 3_000;
const PHONE_FETCH_POLL_TIMEOUT_MS = 30_000;

interface PhoneFetchStatus {
  completed: boolean;
  remaining: number;
  total: number;
  // candidate id -> re-rendered phone button, for the lookups that have landed
  buttons?: Record<string, string>;
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

const csrf = (): string =>
  (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * Polls until every queued lookup has landed, swapping each candidate's phone
 * button in place as its result arrives — the page is never reloaded, so
 * selections, filters and scroll position survive the fetch.
 */
async function waitForPhoneFetches(
  statusUrl: string,
  prospectIds: string[],
  candidateIds: string[],
  status: HTMLElement | null,
): Promise<void> {
  const pending = new Set(candidateIds);
  const startedAt = Date.now();
  let updated = 0;
  let remaining = prospectIds.length;

  while (Date.now() - startedAt < PHONE_FETCH_POLL_TIMEOUT_MS) {
    // The ids travel in the body, not the query string: a full page of
    // candidates is ~100 UUIDs of each kind, which overflows Puma's 10KB
    // QUERY_STRING limit and makes the origin return an unparseable 400.
    const response = await fetch(statusUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf(), Accept: "application/json" },
      body: JSON.stringify({ prospect_ids: prospectIds, bots_prospect_ids: Array.from(pending) }),
    });
    if (!response.ok) throw new Error(String(response.status));

    const data: PhoneFetchStatus = await response.json();
    Object.entries(data.buttons || {}).forEach(([candidateId, html]) => {
      syncRowPhoneButton(candidateId, html);
      pending.delete(candidateId);
      updated += 1;
    });
    remaining = data.remaining;

    if (data.completed) {
      if (status) status.textContent = `Phone lookups complete. Updated ${plural(updated, "candidate")}.`;
      return;
    }

    if (status) status.textContent = `Waiting for ${plural(remaining, "phone lookup")}...`;
    await delay(Math.min(PHONE_FETCH_POLL_INTERVAL_MS, PHONE_FETCH_POLL_TIMEOUT_MS - (Date.now() - startedAt)));
  }

  if (status) {
    status.textContent =
      `Updated ${plural(updated, "candidate")}. ${plural(remaining, "phone lookup")} still processing — ` +
      "run the fetch again later to pick them up.";
  }
}

export function initAllCandidatesBulkPhone(): void {
  if (bound) return;
  bound = true;

  const root = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-all-candidates-bulk]");
  const checkboxes = (): HTMLInputElement[] =>
    Array.from(root()?.querySelectorAll<HTMLInputElement>(".all-candidates-check") || []);
  const isVisible = (checkbox: HTMLInputElement): boolean =>
    checkbox.offsetParent !== null && checkbox.closest("tr")?.style.display !== "none";

  const selectedIds = (): string[] =>
    Array.from(new Set(checkboxes().filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.id || "").filter(Boolean)));

  function update(): void {
    const bulkRoot = root();
    if (!bulkRoot) return;

    const selected = selectedIds();
    const bulkbar = bulkRoot.querySelector<HTMLElement>("[data-all-candidates-bulkbar]");
    const count = bulkRoot.querySelector<HTMLElement>("[data-all-candidates-selected-count]");
    if (count) count.textContent = String(selected.length);
    if (bulkbar) bulkbar.style.display = selected.length ? "flex" : "none";

    bulkRoot.querySelectorAll<HTMLInputElement>(".all-candidates-check-all").forEach((header) => {
      const pane = header.closest<HTMLElement>("[data-view-pane]");
      const visible = Array.from(pane?.querySelectorAll<HTMLInputElement>(".all-candidates-check") || []).filter(isVisible);
      const checked = visible.filter((checkbox) => checkbox.checked).length;
      header.checked = visible.length > 0 && checked === visible.length;
      header.indeterminate = checked > 0 && checked < visible.length;
    });
  }

  function setCandidateSelected(id: string, selected: boolean): void {
    checkboxes().filter((checkbox) => checkbox.dataset.id === id).forEach((checkbox) => { checkbox.checked = selected; });
  }

  document.addEventListener("change", (event) => {
    const target = event.target as HTMLElement;
    const bulkRoot = root();
    if (!bulkRoot || !target || !bulkRoot.contains(target)) return;

    if (target.classList.contains("all-candidates-check")) {
      const checkbox = target as HTMLInputElement;
      if (checkbox.dataset.id) setCandidateSelected(checkbox.dataset.id, checkbox.checked);
      update();
      return;
    }

    if (target.classList.contains("all-candidates-check-all")) {
      const pane = target.closest<HTMLElement>("[data-view-pane]");
      const visible = Array.from(pane?.querySelectorAll<HTMLInputElement>(".all-candidates-check") || []).filter(isVisible);
      visible.forEach((checkbox) => {
        if (checkbox.dataset.id) setCandidateSelected(checkbox.dataset.id, (target as HTMLInputElement).checked);
      });
      update();
    }
  });

  document.addEventListener("click", async (event) => {
    const target = event.target as HTMLElement;
    const bulkRoot = root();
    if (!bulkRoot || !target || !bulkRoot.contains(target)) return;

    if (target.closest("[data-all-candidates-clear]")) {
      checkboxes().forEach((checkbox) => { checkbox.checked = false; });
      update();
      return;
    }

    const button = target.closest<HTMLButtonElement>("[data-bulk-fetch-phone]");
    if (!button) return;

    const ids = selectedIds();
    const url = bulkRoot.dataset.bulkFetchPhoneUrl;
    const statusUrl = bulkRoot.dataset.bulkFetchPhoneStatusUrl;
    if (!ids.length || !url || !statusUrl) return;

    const status = bulkRoot.querySelector<HTMLElement>("[data-all-candidates-bulk-status]");
    button.disabled = true;
    if (status) status.textContent = "Queueing phone number lookups...";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf(), Accept: "application/json" },
        body: JSON.stringify({ bots_prospect_ids: ids }),
      });
      if (!response.ok) throw new Error(String(response.status));

      const data = await response.json();
      const message = data.queued
        ? `Queued ${data.queued} phone number lookup${data.queued === 1 ? "" : "s"}.`
        : "No phone number lookups needed.";
      if (status) status.textContent = data.skipped ? `${message} Skipped ${data.skipped} prospect${data.skipped === 1 ? "" : "s"} with an existing number.` : message;
      checkboxes().forEach((checkbox) => { checkbox.checked = false; });
      update();
      if (data.prospect_ids.length) {
        try {
          await waitForPhoneFetches(statusUrl, data.prospect_ids, data.bots_prospect_ids || [], status);
        } catch (_) {
          if (status) status.textContent = "Phone lookups were queued, but status polling failed. Reload the page to check the latest data.";
        }
      }
    } catch (_) {
      if (status) status.textContent = "Unable to queue phone number lookups. Please try again.";
    } finally {
      button.disabled = false;
    }
  });
}
